/**
 * Contexto e tipos da sessão.
 *
 * Separado do provedor (`sessao.tsx`) porque um arquivo que exporta componente
 * e não-componente ao mesmo tempo quebra o fast refresh do Vite: cada edição
 * remonta a árvore inteira e o formulário que estava preenchido se perde.
 */
import { createContext, useContext } from 'react'

export type Papel = 'administrador' | 'gerente' | 'caixa' | 'vendedor'

export type Usuario = {
  nome: string
  email: string
  papel: Papel
  papel_rotulo: string
  iniciais: string
}

export type Loja = {
  /** A RAZÃO SOCIAL — o nome jurídico, como está no CNPJ. */
  nome: string
  /**
   * O nome que o cliente conhece. Vazio quando a loja não informou, e aí quem
   * aparece na tela é a razão social: cabeçalho sem nome nenhum seria pior que
   * cabeçalho com nome jurídico.
   */
  nome_fantasia?: string
  cnpj: string
  plano: string
  avaliacao_expira_em?: string
  dias_restantes: number
}

/** Nível de uma permissão — spec 04. */
export type Nivel = 'full' | 'pin' | 'read'

export type Sessao = {
  usuario: Usuario
  loja: Loja
  /**
   * Nível por chave de permissão. Chave ausente significa sem acesso.
   *
   * São três comportamentos distintos na tela, e é por isso que um booleano
   * não serve: `full` mostra o botão, `pin` mostra o botão e abre o teclado de
   * PIN ao clicar, `read` esconde o botão e deixa ver.
   */
  niveis: Record<string, Nivel>
  primeiro_acesso: boolean
  email_confirmado: boolean
  expira_em: string
}

export type EstadoSessao =
  | { estado: 'carregando' }
  | { estado: 'anonimo' }
  | { estado: 'autenticado'; sessao: Sessao }

export type ContextoSessao = {
  situacao: EstadoSessao
  /** Guarda a sessão recém-criada pelo login. */
  definir: (sessao: Sessao) => void
  /** Limpa o estado local. Não revoga nada no servidor. */
  limpar: () => void
  /** Revalida contra a API. */
  revalidar: () => Promise<void>
  /** Encerra a sessão no servidor e limpa o estado local. */
  sair: () => Promise<void>
  /**
   * nivelDe devolve o nível do usuário numa permissão, ou 'none'.
   *
   * NÃO é autorização (CONSTITUICAO.md §4.1). Serve para a tela escolher entre
   * mostrar o botão, mostrar e pedir PIN, ou esconder. Se algum dia isto for a
   * única barreira entre um usuário e uma escrita, o furo já está aberto —
   * chamadas de ação são HTTP próprio e não passam por componente nenhum.
   */
  nivelDe: (permissao: string) => Nivel | 'none'
  /** pode diz se a ação sai direto, sem PIN. */
  pode: (permissao: string) => boolean
  /** exigePIN diz se a ação precisa da autorização de um gerente. */
  exigePIN: (permissao: string) => boolean
  /** podeVer diz se o item deve aparecer na navegação. */
  podeVer: (permissao: string) => boolean
}

export const Contexto = createContext<ContextoSessao | null>(null)

export function useSessao(): ContextoSessao {
  const contexto = useContext(Contexto)
  if (!contexto) {
    throw new Error('useSessao precisa estar dentro de <ProvedorDeSessao>')
  }
  return contexto
}
